import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import CategoryTabs from './components/CategoryTabs';
import ArticleCard from './components/ArticleCard';
import { categories, getArticlesByCategory } from '@/mocks/articles';

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');

  const handleCategoryChange = useCallback(
    (slug: string) => {
      setActiveCategory(slug);
      setSearchParams(slug === 'all' ? {} : { category: slug });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const catParam = searchParams.get('category');
    if (catParam && categories.some((c) => c.slug === catParam)) {
      setActiveCategory(catParam);
    }
  }, [searchParams]);

  const displayedArticles = getArticlesByCategory(activeCategory);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '文章中心 - 萝卜视频',
    description: '萝卜视频文章中心，提供AI视频创作教程、产品动态、行业洞察、案例分享和技巧攻略等内容。',
    url: 'https://luoboshipin.com/articles',
    isPartOf: {
      '@type': 'WebSite',
      name: '萝卜视频',
      url: 'https://luoboshipin.com/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://luoboshipin.com/' },
        { '@type': 'ListItem', position: 2, name: '文章中心', item: 'https://luoboshipin.com/articles' },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title="文章中心 - 萝卜视频 | AI视频创作教程与行业洞察"
        description="萝卜视频文章中心，提供AI视频创作教程、产品动态、行业洞察、案例分享和技巧攻略。学习智能视频剪辑、AI配音、自动字幕等实用技能。"
        keywords="萝卜视频教程,AI视频创作,视频剪辑教程,AI配音,智能视频生成,短视频创作技巧"
        canonicalUrl="https://luoboshipin.com/articles"
        schemaData={schemaData}
      />

      <Navbar />

      <main className="pt-20 pb-16 md:pb-24 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ name: '首页', path: '/' }, { name: '文章中心' }]} />

            <div className="mt-6 mb-10">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                文章中心
              </h1>
              <p className="text-foreground-600 text-sm">
                探索AI视频创作的最新教程、行业洞察和实用技巧
              </p>
            </div>

            <div className="mb-8">
              <CategoryTabs
                categories={categories}
                activeSlug={activeCategory}
                onSelect={handleCategoryChange}
              />
            </div>

            {displayedArticles.length > 0 ? (
              <div className="space-y-4">
                {displayedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-article-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}